import React from "react";
import { useTable, Column } from "react-table";
import { BsSearch, BsStar, BsStarFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Customer } from "../../../models";
import { getCustomers, toggleCustomerFavorite } from "../customersSlice";
import "./Customers.scss";

const PAGE = 25;

export function Customers() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const customers = useAppSelector((s) => s.customers.customers) || [];
  const loading = useAppSelector((s) => s.customers.getCustomersResult.loading);

  const [search, setSearch] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [sort, setSort] = React.useState<"name" | "past">("name");

  // Debounce search -> reset to page 0.
  const [debounced, setDebounced] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    dispatch(getCustomers({ search: debounced, offset, count: PAGE, sort: sort === "past" ? "past_orders" : undefined }));
  }, [dispatch, debounced, offset, sort]);

  const columns: Array<Column<Customer>> = React.useMemo(() => [
    {
      Header: "★", accessor: "isFavorite",
      Cell: ({ row }: { row: { original: Customer } }) => {
        const c = row.original;
        const Icon = c.isFavorite ? BsStarFill : BsStar;
        return (
          <Icon
            data-testid={`favorite-toggle-${c.id}`}
            className={`favorite-icon ${c.isFavorite ? "on" : ""}`}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); dispatch(toggleCustomerFavorite({ id: c.id, isFavorite: !c.isFavorite })); }}
          />
        );
      },
    },
    { Header: "Name", accessor: (c: Customer) => `${c.firstName} ${c.lastName}`, id: "name" },
    { Header: "Email", accessor: "email" },
    { Header: "Phone", accessor: "phoneNumber" },
    { Header: "Open", accessor: "openOrdersCount" },
    {
      id: "past", accessor: "pastOrdersCount",
      Header: () => (
        <span
          className="sortable-header"
          onClick={() => { setSort((s) => (s === "past" ? "name" : "past")); setOffset(0); }}
        >
          Past{sort === "past" ? " ▾" : ""}
        </span>
      ),
    },
    {
      Header: "", id: "view", accessor: "id",
      Cell: ({ value }: { value: string }) => (
        <button className="view-button" onClick={() => navigate(`/customers/${value}`)}>View</button>
      ),
    },
  ], [dispatch, navigate, sort]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data: customers });

  return (
    <div className="customers-component">
      <div className="header-filters-section">
        <div className="header-row">
          <div className="title-section">
            <h2>Customers <span className="title-count">{customers.length}</span></h2>
            <p className="subtitle">Search customers, favorite the important ones, and view their order history</p>
          </div>
        </div>
        <div className="search-row">
          <div className="search-container">
            <BsSearch className="search-icon" />
            <input placeholder="Search by name, email, or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="table-container">
        <table {...getTableProps()} className="new-table">
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>{hg.headers.map((col) => (<th {...col.getHeaderProps()}>{col.render("Header")}</th>))}</tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => { prepareRow(row); return (
              <tr {...row.getRowProps()}>{row.cells.map((cell) => (<td {...cell.getCellProps()}>{cell.render("Cell")}</td>))}</tr>
            ); })}
          </tbody>
        </table>
        {loading && <div className="loading">Loading…</div>}
        {!loading && customers.length === 0 && <div className="empty-state">No customers found.</div>}
      </div>

      <div className="pagination">
        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Prev</button>
        <button disabled={customers.length < PAGE} onClick={() => setOffset(offset + PAGE)}>Next</button>
      </div>
    </div>
  );
}
